import { OrderOperationEntity } from './orderOperationEntity';

export type PlaceSaveConditions = 'loadingPlace' | 'unloadingPlace';

export type LoadingDateTimeEntity = {
  start_at: string;
  end_at: string;
}

export const ItemPackageStyleOptions = [
  'ケース',
  '袋',
  'はだか',
  'フレコンバック',
  'ドラム類',
  '缶類',
  'パレット',
  'ラック',
  'ばら'] as const;

export type ItemPackageStyle = string;

export const ItemKlassOptions = [
  '農産物',
  '畜産物',
  '水産物',
  '食料品',
  '飲料品',
  '木材',
  '砂利・砂等',
  '金属製品',
  '銅材',
  '建材',
  '電気製品',
  '機械・装置',
  'セメント',
  'セメント製品',
  '紙・パルプ製品',
  '石油製品',
  '化学製品',
  'その他危険物',
  '衣料・雑貨',
  '引越貨物'
] as const;

export type ItemKlass = typeof ItemKlassOptions[number];

export const ItemHandlingOfCargoStyleOptions = [
  '手積',
  '機械積',
  '機械積（フォーク）',
  '機械積（クレーン）'
] as const;

export type ItemHandlingOfCargoStyle = typeof ItemHandlingOfCargoStyleOptions[number];

export const DesignatedTruckCarModelOptions = [
  '平ボディ',
  'バンボディ',
  'ウィングボディ',
  '保冷車',
  '冷凍車',
  '車載車',
  '重機運搬車',
  '危険物運搬車',
  'ダンプ',
  '幌',
  'ユニック',
  '海上コンテナー用',
] as const;

export type DesignatedTruckCarModel = typeof DesignatedTruckCarModelOptions[number];

export const DesignatedTruckFeatureOptions = [
  'パワーゲート',
  '空調',
  'エアサス',
  'スタンション',
] as const;

export type DesignatedTruckFeature = typeof DesignatedTruckFeatureOptions[number];

export const DesignatedTruckFloorSpecificationOptions = [
  '鉄板',
  'ステンレス',
  'ジョルダー',
  '縞板',
] as const;

export type DesignatedTruckFloorSpecification = typeof DesignatedTruckFloorSpecificationOptions[number];

export const DesignatedTruckLoadingPlatformHeightOptions = [
  '標準',
  '低床',
  '中低床',
] as const;

export type DesignatedTruckLoadingPlatformHeight = typeof DesignatedTruckLoadingPlatformHeightOptions[number];

export const DesignatedTruckLoadingPlatformLengthOptions = [
  '大型',
  '中型',
  '小型',
  'ショート',
  'ロング',
] as const;

export type DesignatedTruckLoadingPlatformLength = typeof DesignatedTruckLoadingPlatformLengthOptions[number];

export const DesignatedTruckLoadingPlatformWidthOptions = [
  '標準',
  'セミワイド',
  'ワイド',
] as const;

export type DesignatedTruckLoadingPlatformWidth = typeof DesignatedTruckLoadingPlatformWidthOptions[number];

export const DesignatedTruckKlassOptions = [
  '軽貨物',
  '2t',
  '4t',
  '10t',
  '20t',
] as const;

export type DesignatedTruckKlass = typeof DesignatedTruckKlassOptions[number];

export type TruckDriverIdAndName = {
  id: number,
  name: string,
}

export type LicenseMiniEntity = {
  id: number,
  name: string,
  email: string,
}

type OrderEntityBase = {
  shipper_name: string; // 荷主名
  shipper_contact_person?: string; // 荷主担当者
  shipper_email_address?: string; // 荷主メールアドレス
  shipper_phone_number?: string; // 荷主電話番号
  code?: string; // 注文コード
  memo?: string; // 備考
  designated_truck_car_models?: DesignatedTruckCarModel[]; // 指定車種
  designated_truck_features?: DesignatedTruckFeature[]; // 指定装置・特徴
  designated_truck_floor_specifications?: DesignatedTruckFloorSpecification[]; // 床仕様
  designated_truck_loading_platform_heights?: DesignatedTruckLoadingPlatformHeight[]; // 荷台高
  designated_truck_loading_platform_lengths?: DesignatedTruckLoadingPlatformLength[]; // 荷台長
  designated_truck_loading_platform_widths?: DesignatedTruckLoadingPlatformWidth[]; // 荷台幅
  designated_truck_klasses?: DesignatedTruckKlass[]; // 車両タイプ
  item_count: number; // 数量
  item_can_be_mixed?: boolean; // 混載可否
  item_handling_of_cargo_style?: ItemHandlingOfCargoStyle; // 荷扱い
  item_height_cm: number; // 高さ
  item_klass?: ItemKlass; // 輸送区分
  item_name?: string; // 品名
  item_packing_style?: ItemPackageStyle // 荷姿
  item_total_volume_m3: number; // 総体積
  item_total_weight_kg: number; // 総重量
  item_vertical_width_cm: number; // 幅
  item_width_cm: number; // 横幅
  loading_name: string; // 積地名称
  loading_address: string; // 積地住所
  loading_end_at: string; // 積地締切日時
  loading_start_at: string; // 積地受付開始日時
  loading_staying_seconds?: number; // 積地作業秒
  loading_staying_minutes?: number; // 積地作業分
  unloading_name: string; // 降地名称
  unloading_address: string; // 降地住所
  unloading_end_at: string; // 降地締切日時
  unloading_start_at: string; // 降地受付開始日時
  unloading_staying_seconds?: number; // 降地作業秒
  unloading_staying_minutes?: number; // 降地作業分
  unit: '才' | 'kg';
  driver_name?: string;
  loading_latitude: string;
  loading_longitude: string;
  unloading_latitude: string;
  unloading_longitude: string;
  skip_loading_geocode: boolean;
  skip_unloading_geocode: boolean;
  place_save_condition: PlaceSaveConditions;
  order_loading_specified_datetimes_attributes: LoadingDateTimeEntity[]; // 積地受付時間
  order_unloading_specified_datetimes_attributes: LoadingDateTimeEntity[]; // 降地受付時間
  custom_input_fields: OrderCustomInputField[];
  allowed_truck_ids: number[];
  denied_driver_ids: number[];
  allowed_trucks: TruckDriverIdAndName[];
  denied_drivers: TruckDriverIdAndName[];
  company_id: number;
  company_name: string;
  update_position?: boolean;
  order_billing_amount_attributes: OrderBillingPaymentAmount;
  order_payment_amount_attributes: OrderBillingPaymentAmount;
  order_assignment_attributes?: OrderAssignment;
  registered_license?: LicenseMiniEntity;
};

export type OrderRequestEntity = OrderEntityBase;

export type OrderCustomInputField = {
  key: string;
  value: string;
}

export type OrderSplit = {
  id: number,
  original_item_total_weight_kg: number,
  original_item_total_volume_m3: number,
  original_item_count: number,
}

export type OrderEntity = OrderEntityBase & {
  id: number;
  loading_lat: number;
  loading_lng: number;
  unloading_lat: number;
  unloading_lng: number;
  order_operations_count: number;
  order_operations: OrderOperationEntity[];
  order_split?: OrderSplit;
  sibling_order_ids?: number[];
  was_split?: boolean;
  siblings_are_allocated?: boolean;
};

export type OrderBillingPaymentAmount = {
  id: number;
  basic_fee_yen: number;
  surcharge_fee_yen: number;
  relay_fee_yen: number;
  highway_fee_yen: number;
  loading_fee_yen: number;
  ancillary_fee_yen: number;
  waiting_time_fee_yen: number;
  unloading_fee_yen: number;
  expenses_fee_yen: number;
  ancillary_content: string;
};

export type OrderAssignment = {
  id: number;
  order_id: number;
  assignment?: string;
  use_ltl?: boolean;
  truck_number?: string;
  driver_name?: string;
  carrier_company?: string;
  operationg_carrier?: string;
  hierarchey_level?: number;
  delivery_id?: string;
  planning_license_id?: number;
  planning_license_name?: string;
  selected_order_ids?: number[];
};
